var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { dtcOrganizationId, isDTCOrganization } from "healthex-common/organization.dtos";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { extractOrgLink, willNeverHappen } from "healthex-common/utils";
import { computed, observable, reaction, when } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { effectiveQueryParams } from "./queryParams";
export var UserLoginType;
(function (UserLoginType) {
    UserLoginType["ORG_USER"] = "ORG_USER";
    UserLoginType["PATIENT"] = "PATIENT";
})(UserLoginType || (UserLoginType = {}));
export var LoginPage;
(function (LoginPage) {
    LoginPage["SELECT_USER_LOGIN_TYPE"] = "SELECT_USER_LOGIN_TYPE";
    LoginPage["ORG_USER_LOGIN"] = "ORG_USER_LOGIN";
    LoginPage["PATIENT_LOGIN"] = "PATIENT_LOGIN";
})(LoginPage || (LoginPage = {}));
const LOGIN_ROUTES = [
    NamedRoutes.LOGIN_SELECT,
    NamedRoutes.LOGIN_AS_ORG_USER,
    NamedRoutes.LOGIN_AS_PATIENT,
    NamedRoutes.SMART_CALLBACK,
    NamedRoutes.PATIENT_ACCOUNT_DELETE_CONFIRMATION,
];
const UNAUTHENTICATED_ROUTES = [
    ...LOGIN_ROUTES,
    NamedRoutes.LOGIN_SWITCH_ORG_AS_PATIENT,
    NamedRoutes.LOGIN_REDIRECT_TO_EMBED,
    NamedRoutes.FORGOT_PASSWORD,
    NamedRoutes.RESET_PASSWORD,
    NamedRoutes.EXPIRED_PASSWORD,
    NamedRoutes.DESIGN_SYSTEM_DEMO,
    NamedRoutes.WELCOME,
    NamedRoutes.WELCOME_ORG,
    NamedRoutes.SMART_LAUNCH,
    NamedRoutes.SMART_LAUNCH_PROVIDER,
    NamedRoutes.SMART_FULL_LAUNCH,
    NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
    NamedRoutes.PATIENT_CONSENT_DATA_UPDATE,
    NamedRoutes.PATIENT_IAS_CONSENT,
    NamedRoutes.PATIENT_IAS_DATA_UPDATE,
    NamedRoutes.CLEAR_IAL2_CALLBACK,
    NamedRoutes.FACILITATED_FHIR_CALLBACK,
    NamedRoutes.FASTEN_CALLBACK,
    NamedRoutes.SOCIAL_LOGIN_CALLBACK,
];
export const UNAUTHENTICATED_HEADER_ROUTES = [NamedRoutes.WELCOME, NamedRoutes.PATIENT_ACCOUNT_DELETE_CONFIRMATION];
const LOGIN_ROUTE_NAMES = LOGIN_ROUTES.map((route) => route.routeName);
const UNAUTHENTICATED_ROUTE_NAMES = UNAUTHENTICATED_ROUTES.map((route) => route.routeName);
export const UNAUTHENTICATED_HEADER_ROUTE_NAMES = UNAUTHENTICATED_HEADER_ROUTES.map((route) => route.routeName);
export class LoginUIManager {
    constructor(authManager, routingUIManager, persistence, organizationsManager, provisioningService, studyManager) {
        this.authManager = authManager;
        this.routingUIManager = routingUIManager;
        this.persistence = persistence;
        this.organizationsManager = organizationsManager;
        this.provisioningService = provisioningService;
        this.studyManager = studyManager;
        this.urlOrganization = null;
        this.pendingSetupCompletion = false;
        this.pendingUserChoiceToSwitchOrgLink = null;
        this.rememberedLoginData = persistence.getRememberedLoginData();
        reaction(() => authManager.isLoggedIn, async () => {
            this.rememberLoginDataIfLoggedIn();
            await this.startOrEndLoginFlowIfNeeded();
        }, { fireImmediately: true });
        reaction(() => [this.routingUIManager.currentRoute, this.routingUIManager.currentRouteParams], async ([currentRoute, _currentRouteParams]) => {
            const patientLogoutRoute = NamedRoutes.LOGOUT_AS_PATIENT.routeName;
            if (patientLogoutRoute === (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName)) {
                this.handlePatientLogout();
                return;
            }
            if (this.pendingUserChoiceToSwitchOrgLink) {
                return;
            }
            const orgLink = extractOrgLink(window.location.hash);
            if (orgLink) {
                this.setWindowHash(orgLink.link);
                return;
            }
            await this.loadUrlOrganization();
            await this.startOrEndLoginFlowIfNeeded();
        }, { fireImmediately: true });
    }
    get loginPage() {
        var _a;
        const routeName = (_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName;
        if (routeName === NamedRoutes.LOGIN_SELECT.routeName) {
            return LoginPage.SELECT_USER_LOGIN_TYPE;
        }
        else if (routeName === NamedRoutes.LOGIN_AS_ORG_USER.routeName) {
            return LoginPage.ORG_USER_LOGIN;
        }
        else if (routeName === NamedRoutes.LOGIN_AS_PATIENT.routeName) {
            return LoginPage.PATIENT_LOGIN;
        }
        else {
            return null;
        }
    }
    get urlParams() {
        var _a;
        const params = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a["*"];
        if (!params)
            return { nextPath: null, orgIdOrVanitySubdomain: null };
        const parts = params.split("/");
        let nextPath = null;
        let orgIdOrVanitySubdomain = null;
        if (parts[0].startsWith("next=")) {
            nextPath = decodeURIComponent(params.substring("next=".length));
        }
        else {
            orgIdOrVanitySubdomain = parts[0];
            if (parts.length > 1 && parts[1].startsWith("next=")) {
                const remainingParts = parts.slice(1).join("/");
                nextPath = decodeURIComponent(remainingParts.substring("next=".length));
            }
        }
        return { nextPath, orgIdOrVanitySubdomain };
    }
    completeOrganizationSwitch(shouldSwitchOrganization) {
        if (!this.pendingUserChoiceToSwitchOrgLink) {
            console.warn(`No pending org link to clear`);
            this.routingUIManager.navigateTo(NamedRoutes.PATIENT_SHARING);
            return;
        }
        const { organizationId, link } = this.pendingUserChoiceToSwitchOrgLink;
        if (shouldSwitchOrganization) {
            this.overrideRememberedLoginData(organizationId);
            this.authManager.logout();
            this.setWindowHash(link);
        }
        else {
            this.routingUIManager.navigateTo(NamedRoutes.PATIENT_SHARING);
        }
        this.pendingUserChoiceToSwitchOrgLink = null;
    }
    setWindowHash(link) {
        window.location.hash = link;
    }
    setLoginPage(page, nextPath, organization, defaultOrganizationId) {
        var _a, _b, _c;
        const orgIdOrVanitySubdomain = organization ? (_a = organization.vanitySubdomain) !== null && _a !== void 0 ? _a : organization.id : defaultOrganizationId;
        const currentRouteName = (_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName;
        let nextParam = nextPath;
        const patientLogoutRoute = NamedRoutes.LOGOUT_AS_PATIENT.routeName;
        if (patientLogoutRoute === currentRouteName) {
            const params = effectiveQueryParams();
            nextParam = (_c = params.get("next")) !== null && _c !== void 0 ? _c : nextPath;
        }
        const routesExcludingNextParam = [
            NamedRoutes.PATIENT_DELETE_MY_ACCOUNT.routeName,
            NamedRoutes.PATIENT_ACCOUNT_DELETE_CONFIRMATION.routeName,
        ];
        const shouldAppendNextParam = !routesExcludingNextParam.includes(currentRouteName !== null && currentRouteName !== void 0 ? currentRouteName : "");
        const routeParams = {
            "*": (orgIdOrVanitySubdomain ? `${orgIdOrVanitySubdomain}/` : "") +
                (shouldAppendNextParam && nextParam ? `next=${encodeURIComponent(nextParam)}` : ""),
        };
        if (currentRouteName === NamedRoutes.PATIENT_DELETE_MY_ACCOUNT.routeName) {
            this.routingUIManager.redirectTo(NamedRoutes.PATIENT_ACCOUNT_DELETE_CONFIRMATION, routeParams);
        }
        else if (page === LoginPage.SELECT_USER_LOGIN_TYPE) {
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_SELECT, routeParams);
        }
        else if (page === LoginPage.ORG_USER_LOGIN) {
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_ORG_USER, routeParams);
        }
        else if (page === LoginPage.PATIENT_LOGIN) {
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_PATIENT, routeParams);
        }
    }
    async startOrEndLoginFlowIfNeeded() {
        if (!this.authManager.isLoggedIn && (await this.routeNeedsAuthentication())) {
            void this.startLoginFlow();
        }
        if (this.authManager.isLoggedIn &&
            this.routingUIManager.currentRoute &&
            LOGIN_ROUTE_NAMES.includes(this.routingUIManager.currentRoute.routeName)) {
            this.endLoginFlow();
        }
    }
    async routeNeedsAuthentication() {
        if (!this.routingUIManager.currentRoute)
            return false;
        if (!UNAUTHENTICATED_ROUTE_NAMES.includes(this.routingUIManager.currentRoute.routeName))
            return true;
        return false;
    }
    async startLoginFlow() {
        var _a, _b, _c;
        const nextPath = window.location.hash.substring(1);
        if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.LOGIN_SELECT.routeName) {
            await when(() => this.organization !== NOT_LOADED);
            if (this.organization !== null &&
                this.organization !== NOT_LOADED &&
                this.organization.type !== UserType.HEALTHCARE_ORGANIZATION &&
                this.organization.type !== UserType.EXTERNAL_RESEARCHER) {
                this.setLoginPage(LoginPage.ORG_USER_LOGIN, nextPath);
            }
            else {
                this.setLoginPage(LoginPage.SELECT_USER_LOGIN_TYPE, nextPath);
            }
        }
        else if (((_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName) === NamedRoutes.LOGIN_AS_ORG_USER.routeName) {
            this.setLoginPage(LoginPage.ORG_USER_LOGIN, nextPath);
        }
        else if (((_c = this.routingUIManager.currentRoute) === null || _c === void 0 ? void 0 : _c.routeName) === NamedRoutes.LOGIN_AS_PATIENT.routeName) {
            this.setLoginPage(LoginPage.PATIENT_LOGIN, nextPath);
        }
        else if (this.rememberedLoginData) {
            if (this.rememberedLoginData.userType === UserType.PATIENT) {
                this.setLoginPage(LoginPage.PATIENT_LOGIN, nextPath);
            }
            else {
                this.setLoginPage(LoginPage.ORG_USER_LOGIN, nextPath);
            }
        }
        else {
            this.setLoginPage(LoginPage.SELECT_USER_LOGIN_TYPE, nextPath);
        }
    }
    endLoginFlow() {
        if (this.authManager.shouldShowAccountSetupScreen === true) {
            this.pendingSetupCompletion = true;
            return;
        }
        if (this.urlParams.nextPath) {
            this.setWindowHash(this.urlParams.nextPath);
        }
        else {
            if (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION &&
                this.authManager.permissions.includes(UserPermissions.PROVIDER) &&
                this.authManager.associatedPatientMRN) {
                this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_SEARCH, { mrn: this.authManager.associatedPatientMRN });
            }
            else {
                this.routingUIManager.navigateTo(NamedRoutes.INDEX);
            }
        }
    }
    completeSetupAndEndLogin() {
        this.pendingSetupCompletion = false;
        this.endLoginFlow();
    }
    async selectUserLoginType(userLoginType) {
        if (userLoginType === UserLoginType.ORG_USER) {
            this.setLoginPage(LoginPage.ORG_USER_LOGIN);
        }
        else if (userLoginType === UserLoginType.PATIENT) {
            this.setLoginPage(LoginPage.PATIENT_LOGIN, undefined);
        }
        else {
            willNeverHappen(userLoginType);
        }
    }
    isUnauthenticatedRoute(route) {
        return UNAUTHENTICATED_ROUTE_NAMES.includes(route.routeName);
    }
    isUnauthenticatedHeaderRoute(route) {
        return UNAUTHENTICATED_HEADER_ROUTE_NAMES.includes(route.routeName);
    }
    async loadUrlOrganization() {
        var _a;
        const loadRoutes = [
            NamedRoutes.LOGIN_SELECT.routeName,
            NamedRoutes.LOGIN_AS_ORG_USER.routeName,
            NamedRoutes.LOGIN_AS_PATIENT.routeName,
        ];
        const routeName = (_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName;
        if (routeName && loadRoutes.includes(routeName)) {
            let orgIdOrVanitySubdomain = this.urlParams.orgIdOrVanitySubdomain;
            if (!orgIdOrVanitySubdomain && routeName === NamedRoutes.LOGIN_AS_PATIENT.routeName) {
                orgIdOrVanitySubdomain = dtcOrganizationId;
                console.log("patient orgIdOrVanitySubdomain defaulting to DTC organization");
            }
            if (orgIdOrVanitySubdomain) {
                this.urlOrganization = NOT_LOADED;
                let urlOrganization = null;
                if (isDTCOrganization(orgIdOrVanitySubdomain) ||
                    (orgIdOrVanitySubdomain.length === 36 && orgIdOrVanitySubdomain.includes("-"))) {
                    urlOrganization = await this.provisioningService.getOrganizationPublicProfileById(orgIdOrVanitySubdomain);
                }
                if (!urlOrganization) {
                    const matches = await this.provisioningService.getOrganizationPublicProfileByVanitySubdomain(orgIdOrVanitySubdomain);
                    urlOrganization = matches.length ? matches[0] : null;
                }
                this.urlOrganization = urlOrganization;
                return;
            }
        }
        this.urlOrganization = null;
    }
    get organization() {
        var _a;
        if (this.urlOrganization) {
            return this.urlOrganization;
        }
        else if (this.rememberedLoginData) {
            const organizationId = this.rememberedLoginData.organizationId;
            const organizations = this.organizationsManager.organizationPublicProfiles.value;
            if (organizations === NOT_LOADED)
                return NOT_LOADED;
            return (_a = organizations.find((candidate) => candidate.id === organizationId)) !== null && _a !== void 0 ? _a : null;
        }
        else {
            return null;
        }
    }
    get previousAuthenticationProvider() {
        var _a, _b;
        return (_b = (_a = this.rememberedLoginData) === null || _a === void 0 ? void 0 : _a.gfidProvider) !== null && _b !== void 0 ? _b : null;
    }
    rememberLoginDataIfLoggedIn() {
        if (!this.authManager.userType)
            return;
        this.rememberedLoginData = {
            organizationId: this.authManager.orgId,
            userType: this.authManager.userType,
            gfidProvider: this.authManager.gfidProvider,
        };
        this.persistence.setRememberedLoginData(this.rememberedLoginData);
    }
    overrideRememberedLoginData(organizationId) {
        var _a;
        this.rememberedLoginData = {
            userType: UserType.PATIENT,
            organizationId,
            gfidProvider: (_a = this.rememberedLoginData) === null || _a === void 0 ? void 0 : _a.gfidProvider,
        };
        this.persistence.setRememberedLoginData(this.rememberedLoginData);
    }
    get justLoggedIn() {
        return this.routingUIManager.previousRoute && LOGIN_ROUTE_NAMES.includes(this.routingUIManager.previousRoute.routeName);
    }
    handlePatientLogout() {
        const params = effectiveQueryParams();
        const resetClearsFallbackData = params.get("reset") === "true";
        if (resetClearsFallbackData)
            this.persistence.setClearFallbackData(null);
        if (this.authManager.isLoggedIn) {
            this.authManager.logout();
        }
        else {
            this.setLoginPage(LoginPage.PATIENT_LOGIN);
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], LoginUIManager.prototype, "urlOrganization", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], LoginUIManager.prototype, "rememberedLoginData", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], LoginUIManager.prototype, "pendingUserChoiceToSwitchOrgLink", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], LoginUIManager.prototype, "pendingSetupCompletion", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], LoginUIManager.prototype, "loginPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], LoginUIManager.prototype, "urlParams", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], LoginUIManager.prototype, "organization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], LoginUIManager.prototype, "previousAuthenticationProvider", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], LoginUIManager.prototype, "justLoggedIn", null);
